import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import MuiTable from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import { Translate, TextFormat, getPaginationState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getTours } from 'app/entities/tour/tour.reducer';
import { getEntities } from './tour-step.reducer';

export const TourStep = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );
  const [selectedTour, setSelectedTour] = useState<string>('');

  const tours = useAppSelector(state => state.tour.entities);
  const tourStepList = useAppSelector(state => state.tourStep.entities);
  const loading = useAppSelector(state => state.tourStep.loading);
  const totalItems = useAppSelector(state => state.tourStep.totalItems);

  useEffect(() => {
    dispatch(getTours({ size: 1000 }));
  }, []);

  const getAllEntities = () => {
    const sort = selectedTour ? 'stepOrder,asc' : `${paginationState.sort},${paginationState.order}`;
    const params: any = {
      page: paginationState.activePage - 1,
      size: paginationState.itemsPerPage,
      sort,
    };
    if (selectedTour) {
      params.tourId = Number(selectedTour);
    }
    dispatch(getEntities(params));
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, selectedTour]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleTourFilterChange = (event: any) => {
    const value = event.target.value;
    setSelectedTour(value);
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="tour-step-heading" data-cy="TourStepHeading">
        <Translate contentKey="jhipsterSampleApplicationApp.tourStep.home.title">Tour Steps</Translate>
        <div className="d-flex justify-content-end">
          <Button
            className="me-2"
            onClick={handleSyncList}
            disabled={loading}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon="sync" spin={loading} />}
          >
            <Translate contentKey="jhipsterSampleApplicationApp.tourStep.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Button
            component={Link}
            to="/tour-step/new"
            variant="contained"
            color="primary"
            id="jh-create-entity"
            data-cy="entityCreateButton"
            startIcon={<FontAwesomeIcon icon="plus" />}
          >
            <Translate contentKey="jhipsterSampleApplicationApp.tourStep.home.createLabel">Create new Tour Step</Translate>
          </Button>
        </div>
      </h2>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel id="tour-step-filter-tour-label">
            <Translate contentKey="jhipsterSampleApplicationApp.tourStep.tour">Tour</Translate>
          </InputLabel>
          <Select
            labelId="tour-step-filter-tour-label"
            id="tour-step-filter-tour"
            value={selectedTour}
            label={<Translate contentKey="jhipsterSampleApplicationApp.tourStep.tour">Tour</Translate>}
            onChange={handleTourFilterChange}
          >
            <MenuItem value="">
              <em>All Tours</em>
            </MenuItem>
            {tours
              ? tours.map(tour => (
                  <MenuItem value={tour.id} key={tour.id}>
                    {tour.code}
                  </MenuItem>
                ))
              : null}
          </Select>
        </FormControl>
        {selectedTour && (
          <Button
            variant="text"
            color="error"
            size="small"
            onClick={() => {
              setSelectedTour('');
              setPaginationState({ ...paginationState, activePage: 1 });
            }}
          >
            Clear Filter
          </Button>
        )}
      </Box>
      <div className="table-responsive">
        {tourStepList && tourStepList.length > 0 ? (
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <MuiTable>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('id')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourStep.id">ID</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('code')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourStep.code">Code</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('code')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('enabled')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourStep.enabled">Enabled</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('enabled')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('icon')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourStep.icon">Icon</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('icon')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('stepOrder')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourStep.stepOrder">Step Order</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('stepOrder')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('waitTime')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourStep.waitTime">Wait Time</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('waitTime')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('waitTimeMeasure')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourStep.waitTimeMeasure">Wait Time Measure</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('waitTimeMeasure')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('driveTime')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourStep.driveTime">Drive Time</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('driveTime')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('driveTimeMeasure')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourStep.driveTimeMeasure">Drive Time Measure</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('driveTimeMeasure')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('createdDate')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourStep.createdDate">Created Date</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('createdDate')} />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourStep.createdBy">Created By</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourStep.tour">Tour</Translate> <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourStep.place">Place</Translate> <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {tourStepList.map((tourStep, i) => (
                  <TableRow key={`entity-${i}`} data-cy="entityTable">
                    <TableCell>
                      <Button component={Link} to={`/tour-step/${tourStep.id}`} variant="text" size="small">
                        {tourStep.id}
                      </Button>
                    </TableCell>
                    <TableCell>{tourStep.code}</TableCell>
                    <TableCell>{tourStep.enabled ? 'true' : 'false'}</TableCell>
                    <TableCell>{tourStep.icon}</TableCell>
                    <TableCell>{tourStep.stepOrder}</TableCell>
                    <TableCell>{tourStep.waitTime}</TableCell>
                    <TableCell>
                      <Translate contentKey={`jhipsterSampleApplicationApp.DurationMeasure.${tourStep.waitTimeMeasure}`} />
                    </TableCell>
                    <TableCell>{tourStep.driveTime}</TableCell>
                    <TableCell>
                      <Translate contentKey={`jhipsterSampleApplicationApp.DurationMeasure.${tourStep.driveTimeMeasure}`} />
                    </TableCell>
                    <TableCell>
                      {tourStep.createdDate ? <TextFormat type="date" value={tourStep.createdDate} format={APP_LOCAL_DATE_FORMAT} /> : null}
                    </TableCell>
                    <TableCell>{tourStep.createdBy ? tourStep.createdBy.login : ''}</TableCell>
                    <TableCell>{tourStep.tour ? <Link to={`/tour/${tourStep.tour.id}`}>{tourStep.tour.code}</Link> : ''}</TableCell>
                    <TableCell>{tourStep.place ? <Link to={`/place/${tourStep.place.id}`}>{tourStep.place.code}</Link> : ''}</TableCell>
                    <TableCell className="text-end">
                      <ButtonGroup variant="outlined" size="small" aria-label="entity actions">
                        <Button
                          component={Link}
                          to={`/tour-step/${tourStep.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                          color="primary"
                          startIcon={<FontAwesomeIcon icon="pencil-alt" />}
                        >
                        </Button>
                        <Button
                          onClick={() =>
                            (window.location.href = `/tour-step/${tourStep.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                          }
                          color="error"
                          startIcon={<FontAwesomeIcon icon="trash" />}
                        >
                        </Button>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="jhipsterSampleApplicationApp.tourStep.home.notFound">No Tour Steps found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={tourStepList && tourStepList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default TourStep;
